
/* eslint-disable no-unused-vars */
import { defineComponent, ref, Ref, onMounted, inject, computed } from "vue";
import { fabric } from "fabric";

import { store } from "@/store";

import { removeActiveObject } from "@/canvas-api/Canvas";

import CloseSVG from "@/components/CloseSVG.vue";

import useMountCanvas from "@/composables/useMountCanvas";
import { addNewImage, imgString, getBackUpImage } from "@/canvas-api/UserObjects/Image";

interface verifiedObject extends fabric.Object {
  data: fabric.Object;
  width: number;
  height: number;
  scaleX: number;
  scaleY: number;
  left: number;
  top: number;
}

function verifyFabricObject(object: fabric.Object | undefined): object is verifiedObject {
  if (
    object &&
    object.width &&
    object.height &&
    object.scaleX &&
    object.scaleY &&
    object.left &&
    object.top
  ) {
    return true;
  } else {
    return false;
  }
}

export default defineComponent({
  name: "CropImageModal",
  components: {
    CloseSVG,
  },
  emits: ["closeModalAndCleanInput"],
  setup(_, { emit }) {
    const handleModal = inject("handleModal");
    const bounceTransition = inject("bounceTransition") as Ref<string>;
    const fadeTransition = inject("fadeTransition") as Ref<string>;

    const modalBody = ref<HTMLDivElement>();
    const canvasRef = ref<HTMLCanvasElement>();
    const modalCanvas = ref<fabric.Canvas>(new fabric.Canvas(null));
    const canvasWidth = ref(450);
    const canvasHeight = ref(450);

    const mainImg = ref(new fabric.Image() as fabric.Image);
    const imgAsModalBg = ref(new fabric.Image() as fabric.Image);

    const isMobile = computed(() => store.getters.isMobile);
    const currentActiveObject = computed(() => store.getters.currentActiveObject);

    onMounted(() => {
      if (isMobile.value && modalBody.value) {
        const modalBodyBCR = modalBody.value.getBoundingClientRect();
        const size = modalBodyBCR.width * 0.9;

        modalCanvas.value = useMountCanvas(modalCanvas.value, canvasRef.value, size, size, "");

        modalCanvas.value.preserveObjectStacking = true;

        addImageToCropCanvas(modalCanvas.value, size, size);
      } else {
        modalCanvas.value = useMountCanvas(
          modalCanvas.value,
          canvasRef.value,
          canvasWidth.value,
          canvasHeight.value,
          ""
        );
        modalCanvas.value.preserveObjectStacking = true;
        addImageToCropCanvas(modalCanvas.value, canvasWidth.value, canvasHeight.value);
      }
    });

    const addImageToCropCanvas = (
      canvas: fabric.Canvas,
      canvasWidth: number,
      canvasHeight: number
    ): void => {
      let canvasCenter = canvas.getCenter();
      let imgInstance = new Image();

      imgInstance.onload = function() {
        imgAsModalBg.value = new fabric.Image(imgInstance, {
          left: canvasCenter.left,
          top: canvasCenter.top,
          originX: "center",
          originY: "center",
          name: "mainImg",
          selectable: false,
          evented: false,
          // globalCompositeOperation: "source-over"
        }) as fabric.Image;

        mainImg.value = new fabric.Image(imgInstance, {
          left: canvasCenter.left,
          top: canvasCenter.top,
          originX: "center",
          originY: "center",
          name: "mainImg",
          selectable: false,
          evented: false,
        }) as fabric.Image;

        if (imgAsModalBg.value.width! > imgAsModalBg.value.height!) {
          imgAsModalBg.value.scaleToWidth(canvasWidth);
        } else {
          imgAsModalBg.value.scaleToHeight(canvasWidth);
        }

        canvas.setWidth(imgAsModalBg.value.width! * imgAsModalBg.value.scaleX!);
        canvas.setHeight(imgAsModalBg.value.height! * imgAsModalBg.value.scaleY!);

        canvasCenter = canvas.getCenter();

        canvas.setOverlayImage(imgAsModalBg.value, () => {}, {
          left: canvasCenter.left,
          top: canvasCenter.top,
          originX: "center",
          originY: "center",
          width: imgAsModalBg.value.width,
          height: imgAsModalBg.value.height,
          scaleX: imgAsModalBg.value.scaleX,
          scaleY: imgAsModalBg.value.scaleY,
          globalCompositeOperation: "destination-atop",
        });

        let overlay = new fabric.Rect({
          left: canvasCenter.left,
          top: canvasCenter.top,
          originX: "center",
          originY: "center",
          width: canvasWidth,
          height: canvasHeight,
          fill: "black",
          opacity: 0.5,
          selectable: false,
          globalCompositeOperation: "source-over",
        });

        let punchOut = new fabric.Rect({
          left: canvasCenter.left,
          top: canvasCenter.top,
          hasRotatingPoint: false,
          originX: "center",
          originY: "center",
          width: 100,
          height: 100,
          name: "punchOut",
          globalCompositeOperation: "destination-out",
        }).setControlsVisibility({ mtr: false });

        punchOut.showAngleDisplayControl(false);

        canvas.add(...[overlay, punchOut]);
        canvas.setActiveObject(punchOut);
        canvas.renderAll();
      };

      if (currentActiveObject.value && currentActiveObject.value.name === "image") {
        const activeObj = currentActiveObject.value as fabric.Image;

        try {
          imgInstance.src = getBackUpImage(activeObj.imageId!); //The ability to crop images is only provided to images with imageIds. See HTML in EditImage.vue
        } catch (e) {
          console.error(e);
        }
      } else {
        imgInstance.src = imgString.value;
      }
    };

    const cropImage = (): void => {
      let punchOut = modalCanvas.value._objects.find(
        (obj: fabric.Object) => obj.name === "punchOut"
      );

      if (verifyFabricObject(punchOut) && verifyFabricObject(imgAsModalBg.value)) {
        const modalBgWidth = imgAsModalBg.value.width;
        const modalBgHeight = imgAsModalBg.value.height;

        const scaledImgWidth = imgAsModalBg.value.width * imgAsModalBg.value.scaleX;
        const scaledImgHeight = imgAsModalBg.value.height * imgAsModalBg.value.scaleY;

        const scaleDownX = modalBgWidth / scaledImgWidth;
        const scaleDownY = modalBgHeight / scaledImgHeight;

        const punchOutWidth = Math.floor(punchOut.width * punchOut.scaleX) * scaleDownX;
        const punchOutHeight = Math.floor(punchOut.height * punchOut.scaleY) * scaleDownY;

        const left = punchOut.left * scaleDownX - punchOutWidth / 2;
        const top = punchOut.top * scaleDownY - punchOutHeight / 2;

        const cropOptions = {
          left,
          top,
          width: punchOutWidth,
          height: punchOutHeight,
        };

        const cropDataUrl = mainImg.value.toDataURL(cropOptions);

        if (currentActiveObject.value && currentActiveObject.value.name === "image") {
          removeActiveObject();
        }

        fabric.Image.fromURL(cropDataUrl, function(img) {
          addNewImage(img as fabric.Image, cropDataUrl);
          emit("closeModalAndCleanInput");
        });
      } else {
        console.log("Error when cropping image");
      }
    };

    const noCrop = (): void => {
      const cropOptions = {
        width: mainImg.value.width! * mainImg.value.scaleX!,
        height: mainImg.value.height! * mainImg.value.scaleY!,
      };

      if (currentActiveObject.value && currentActiveObject.value.name === "image") {
        removeActiveObject();
      }

      addNewImage(mainImg.value, mainImg.value.toDataURL(cropOptions));
      emit("closeModalAndCleanInput");
    };

    return {
      handleModal,
      bounceTransition,
      fadeTransition,
      modalBody,
      canvasRef,
      cropImage,
      noCrop,
    };
  },
});
